// The block registry. A block definition describes how PageFlow recognizes an element at import,
// creates a new one, prepares it for editing, cleans it at export and which settings it offers.
//
// {
//   name: 'image',                 unique name, stored in data-pf while editing
//   label: 'block.image',          i18n key (or plain text)
//   icon: 'image',                 key of uiIcons
//   category: 'basic',             palette group: layout, basic, media, components, advanced
//   kind: 'leaf',                  leaf | flow (holds blocks) | row (holds columns) | col
//   priority: 50,                  higher is tried first at import
//   match(el, ctx) => bool,        recognizes an imported element (flow position)
//   create(ctx) => Element,        a new instance, built in the canvas document
//   prepare(el, ctx),              marks editable regions after import or creation
//   exportNode(el, ctx),           cleans the exported clone (in place)
//   fields: [...] | (el, ctx) => [...],  settings of the "Content" panel
//   accepts(name) => bool,         for flow blocks: which blocks may go inside
//   presets: [{id, label, icon, html}],  extra palette entries built from HTML
//   palette: true,                 shown in the palette
// }

const definitions = new Map();

export function registerBlock(definition) {
    if (!definition || !definition.name) throw new Error('PageFlow: a block needs a name.');
    const def = {
        kind: 'leaf',
        category: 'basic',
        priority: 50,
        palette: true,
        icon: 'square',
        fields: [],
        ...definition,
    };
    definitions.set(def.name, def);
    return def;
}

export function unregisterBlock(name) {
    definitions.delete(name);
}

export function getBlock(name) {
    return definitions.get(name) || null;
}

export function allBlocks() {
    return Array.from(definitions.values());
}

/** Definitions tried at import, the most specific first. */
export function importOrder() {
    return allBlocks()
        .filter(def => typeof def.match === 'function')
        .sort((a, b) => b.priority - a.priority);
}

export function blockOf(el) {
    return el && el.getAttribute ? getBlock(el.getAttribute('data-pf')) : null;
}

/** Whether a block named `child` may be placed inside the element `parent` (or the root). */
export function canContain(parent, child, isRoot = false) {
    const childDef = getBlock(child);
    if (!childDef) return false;
    if (isRoot) return childDef.kind !== 'col';
    const parentDef = blockOf(parent);
    if (!parentDef) return false;
    if (parentDef.kind === 'row') return childDef.kind === 'col';
    if (parentDef.kind !== 'flow' && parentDef.kind !== 'col') return false;
    if (childDef.kind === 'col') return false;
    return typeof parentDef.accepts === 'function' ? parentDef.accepts(child) : true;
}
